
// src/components/planning/EditSelectionsModal/components/PlacementSearchInput/PlacementSearchInput.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { searchPlacements } from '../../services/placementService';
import styles from './PlacementSearchInput.module.css';

interface PlacementSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

export const PlacementSearchInput: React.FC<PlacementSearchInputProps> = ({
  value,
  onChange,
  onBlur
}) => {
  const safeValue = value || '';
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const fetchSuggestions = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const placements = await searchPlacements(searchTerm);
      setSuggestions(placements);
    } catch (error) {
      console.error('Error fetching placement suggestions:', error);
      setSuggestions([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout to fetch suggestions
    timeoutRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);

    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
    inputRef.current?.blur();
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow for click events
    setTimeout(() => {
      setShowSuggestions(false);
      onBlur?.();
    }, 200);
  };

  return (
    <div className={styles.container}>
      <Form.Control
        ref={inputRef}
        type="text"
        value={safeValue}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onBlur={handleBlur}
        placeholder="Enter placement..."
        className={styles.input}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className={styles.suggestionsContainer}>
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion}-${index}`}
              className={styles.suggestionItem}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent input blur
                handleSuggestionClick(suggestion);
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
